import { Box, FormControl, FormHelperText, Stack } from '@mui/material'
import { useTheme } from '@thriveglobal/thrive-web-core'
import {
    CheckboxConsent,
    CoreTypography
} from '@thriveglobal/thrive-web-leafkit'
import React, { useCallback, useMemo, useState } from 'react'
import {
    Controller,
    ControllerRenderProps,
    FieldValues,
    useFormContext
} from 'react-hook-form'
import { defineMessages, useIntl } from 'react-intl'
import { TermsOfServiceOption } from '../../../../../graphql/generated/autogenerated'

const messages = defineMessages({
    tosAcceptanceDialogBody: {
        defaultMessage:
            'Please agree to the Privacy Policy and Terms of Service before proceeding with Thrive.',
        description:
            'Please agree to the Privacy Policy and Terms of Service before proceeding with Thrive.'
    },
    tosApacAcceptance: {
        defaultMessage:
            'By providing your consent, you agree to the collection and use of your personal information to authenticate your account, deliver and improve Thrive’s services, and communicate with you. Personal data may include your name, email address, engagement, and, if applicable, any activity data from connected devices. Your data will be retained only as long as necessary to fulfill these purposes. You may withdraw consent or request changes to your data at any time.',
        description: 'description of what clicking accept grants'
    },
    agreeToToS: {
        defaultMessage:
            'I agree to the <privacyPolicy>Privacy Policy</privacyPolicy> and the <termsOfService>Terms of Service</termsOfService>',
        description: 'Message to accept the Privacy Policy and Terms of Service'
    },
    thrivePolicyRequired: {
        defaultMessage: 'Accept privacy policy and terms of service',
        description:
            'Message shown when a user needs to select the Terms of Service'
    },
    policyRequired: {
        defaultMessage: 'You must accept the {policyName} policy.',
        description: 'Error message for unaccepted required policies'
    },
    policyReadRequired: {
        defaultMessage:
            "Please review the Authorization for {companyName}'s Disclosure of Health Information before continuing.",
        description: 'Error message for unread required policies'
    }
})

const APAC_COUNTRIES = ['KR', 'VN']

const REQUIRED_TERMS_CHECKBOXES = ['generalData', 'sensitiveData']

export type PolicyChecks = { policyChecks: PolicyStatuses }
export type PolicyStatuses = { [key: string]: PolicyStatus }
export type PolicyStatus = { url: string; accepted: boolean; read: boolean }
export const DEFAULT_POLICY_STATUS: PolicyChecks = {
    policyChecks: {
        thrivePolicy: { accepted: false, read: false, url: '' }
    }
}
export interface ToSAcceptanceFormControllerProps
    extends JSX.IntrinsicAttributes {
    country?: string
    companyName?: string
    additionalCheckboxPolicies?: TermsOfServiceOption[]
}

const ToSAcceptanceFormController: React.FC<
    ToSAcceptanceFormControllerProps
> = ({ country, companyName, additionalCheckboxPolicies }) => {
    const { formatMessage } = useIntl()
    const { breakpoints } = useTheme()

    const {
        control,
        setValue,
        formState: { errors }
    } = useFormContext()

    const setControlValue = useCallback(
        (
            field: ControllerRenderProps<FieldValues, 'policyChecks'>,
            shouldValidate: boolean,
            value: PolicyStatuses
        ) => {
            setValue(
                'policyChecks',
                { ...field.value, ...value },
                {
                    shouldValidate
                }
            )
        },
        [setValue]
    )

    const countryIsAPAC = useMemo(
        () => APAC_COUNTRIES.includes(country ?? ''),
        [country]
    )

    return (
        <Controller
            name="policyChecks"
            control={control}
            rules={{
                validate: {
                    policyRequired: (value) => {
                        if (!value.thrivePolicy.accepted) {
                            return formatMessage(messages.thrivePolicyRequired)
                        } else if (additionalCheckboxPolicies?.length > 0) {
                            const unaccepted = additionalCheckboxPolicies.find(
                                (checkbox) =>
                                    checkbox.checkboxRequired &&
                                    !value[checkbox.name]?.accepted
                            )

                            if (unaccepted) {
                                return formatMessage(messages.policyRequired, {
                                    policyName: unaccepted.name
                                })
                            }
                        }
                    },
                    policyReadRequired: (value) => {
                        if (additionalCheckboxPolicies?.length > 0) {
                            const unread = additionalCheckboxPolicies.find(
                                (checkbox) =>
                                    checkbox.readRequired &&
                                    !value[checkbox.name]?.read
                            )

                            if (unread) {
                                return formatMessage(
                                    messages.policyReadRequired,
                                    {
                                        companyName: companyName
                                    }
                                )
                            }
                        }

                        return true
                    }
                }
            }}
            render={({ field }) => {
                return (
                    <Stack>
                        <CoreTypography pb={1} variant="body1">
                            {formatMessage(
                                countryIsAPAC
                                    ? messages.tosApacAcceptance
                                    : messages.tosAcceptanceDialogBody
                            )}
                        </CoreTypography>
                        <FormControl
                            required
                            component="fieldset"
                            variant="standard"
                            error={Boolean(errors?.policyChecks)}
                        >
                            <Box
                                sx={{
                                    maxWidth: breakpoints.values.md,
                                    textAlign: 'start'
                                }}
                            >
                                <CheckboxConsent
                                    country={country}
                                    companyName={companyName}
                                    additionalCheckboxes={additionalCheckboxPolicies?.map(
                                        (checkbox) => ({
                                            policyName: checkbox.name,
                                            policyLink: checkbox.url,
                                            checkboxRequired:
                                                checkbox.checkboxRequired,
                                            readRequired: checkbox.readRequired,
                                            checked:
                                                field.value[checkbox.name]
                                                    ?.accepted,
                                            onAccepted: (accepted) => {
                                                setControlValue(field, true, {
                                                    [checkbox.name]: {
                                                        url: checkbox.url,
                                                        accepted: accepted,
                                                        read:
                                                            field.value[
                                                                checkbox.name
                                                            ]?.read ?? false
                                                    }
                                                })
                                            },
                                            onRead: (read) => {
                                                setControlValue(field, true, {
                                                    [checkbox.name]: {
                                                        url: checkbox.url,
                                                        accepted:
                                                            field.value[
                                                                checkbox.name
                                                            ]?.accepted ??
                                                            false,
                                                        read: read
                                                    }
                                                })
                                            }
                                        })
                                    )}
                                    ref={field.ref}
                                    defaultValue={
                                        field.value.thrivePolicy?.accepted
                                    }
                                    onAccepted={(accepted, state) => {
                                        const valid = state
                                            ? REQUIRED_TERMS_CHECKBOXES.every(
                                                  (key) => !!state[key]
                                              )
                                            : false
                                        setControlValue(field, valid, {
                                            thrivePolicy: {
                                                accepted: accepted,
                                                // Thrive doesn't require any of the below props so we just default them
                                                read: false,
                                                url: ''
                                            }
                                        })
                                    }}
                                />
                            </Box>
                            <FormHelperText
                                variant="standard"
                                id="accept-TnC-helper-text"
                            >
                                {
                                    errors?.policyChecks
                                        ?.message as unknown as React.ReactNode
                                }
                            </FormHelperText>
                        </FormControl>
                    </Stack>
                )
            }}
        />
    )
}
export default ToSAcceptanceFormController
